import { supabase } from '../SupabaseClient/supabase-client';

export type TargetAssetRecord = {
  id: string;
  assetClassId: string;
  ticker: string;
  rating: number;
  quantity: number;
  averagePrice: number;
  updatedAt?: string;
};

export type TargetPortfolioAsset = {
  id: string;
  ticker: string;
  rating: number;
  quantity: number;
  averagePrice: number;
  updatedAt?: string;
  logoUrl?: string;
  price?: number | null;
  shortName?: string | null;
  amountInvested: number | null;
  amountInvestedIsEstimated: boolean;
  currentValue: number | null;
};

export type TargetPortfolioClass = {
  id: string;
  code: string;
  percentage: number;
  assets: TargetPortfolioAsset[];
};

export type TargetPortfolioResponse = {
  classes: TargetPortfolioClass[];
};

const TABLE = 'target_assets';

const clampRating = (value: number) => {
  if (Number.isNaN(value)) return 0;
  return Math.min(10, Math.max(0, Math.round(value)));
};

const clampQuantity = (value: number) => {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, parsed);
};

const mapRow = (
  row:
    | {
        id?: string | number;
        asset_class_id?: string | number;
        ticker?: string;
        rating?: number;
        quantity?: number;
        average_price?: number;
        updated_at?: string;
      }
    | null,
): TargetAssetRecord | null => {
  if (!row) return null;

  const id = typeof row.id === 'string' ? row.id : row.id !== undefined ? String(row.id) : '';
  const assetClassId =
    typeof row.asset_class_id === 'string'
      ? row.asset_class_id
      : row.asset_class_id !== undefined
        ? String(row.asset_class_id)
        : '';
  if (!id || !assetClassId) return null;

  const rating = clampRating(
    typeof row.rating === 'number' ? row.rating : Number(row.rating ?? 0),
  );
  const quantity = clampQuantity(
    typeof row.quantity === 'number' ? row.quantity : Number(row.quantity ?? 0),
  );
  const averagePrice = Number(row.average_price ?? 0);

  return {
    id,
    assetClassId,
    ticker: (row.ticker ?? '').toString().toUpperCase(),
    rating,
    quantity,
    averagePrice: Number.isFinite(averagePrice) ? averagePrice : 0,
    updatedAt: row.updated_at,
  };
};

let cachedUserId: string | null = null;

supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
});

const getUserId = async (): Promise<string> => {
  if (cachedUserId) return cachedUserId;

  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    cachedUserId = null;
    throw new Error('Usuário não autenticado');
  }

  cachedUserId = data.user.id;
  return cachedUserId;
};

export const targetAssetsService = {
  async getTargetPortfolio(): Promise<TargetPortfolioResponse> {
    const { data, error } = await supabase.functions.invoke('target-portfolio');
    if (error) throw error;
    return data as TargetPortfolioResponse;
  },

  async list(): Promise<TargetAssetRecord[]> {
    const userId = await getUserId();
    const { data, error } = await supabase
      .from(TABLE)
      .select('id,asset_class_id,ticker,rating,quantity,average_price,updated_at')
      .eq('user_id', userId);

    if (error) throw error;
    return (data ?? []).map(mapRow).filter(Boolean) as TargetAssetRecord[];
  },

  async listByClass(assetClassId: string): Promise<TargetAssetRecord[]> {
    const userId = await getUserId();
    const { data, error } = await supabase
      .from(TABLE)
      .select('id,asset_class_id,ticker,rating,quantity,average_price,updated_at')
      .eq('user_id', userId)
      .eq('asset_class_id', assetClassId);

    if (error) throw error;
    return (data ?? []).map(mapRow).filter(Boolean) as TargetAssetRecord[];
  },

  async create(input: {
    assetClassId: string;
    ticker: string;
    rating?: number;
    quantity?: number;
    averagePrice?: number;
  }): Promise<TargetAssetRecord> {
    const userId = await getUserId();
    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        asset_class_id: input.assetClassId,
        ticker: input.ticker.toUpperCase(),
        rating: clampRating(Number(input.rating ?? 0)),
        quantity: clampQuantity(Number(input.quantity ?? 0)),
        average_price: Math.max(0, Number(input.averagePrice ?? 0)),
        user_id: userId,
      })
      .select('id,asset_class_id,ticker,rating,quantity,average_price,updated_at')
      .single();

    if (error) throw error;
    const mapped = mapRow(data);
    if (!mapped) throw new Error('Registro de ativo invalido');
    return mapped;
  },

  async update(record: {
    id: string;
    assetClassId?: string;
    ticker?: string;
    rating?: number;
    quantity?: number;
    averagePrice?: number;
  }): Promise<TargetAssetRecord> {
    const userId = await getUserId();
    const payload: Record<string, unknown> = {};
    if (record.assetClassId) payload.asset_class_id = record.assetClassId;
    if (record.ticker !== undefined) payload.ticker = record.ticker.toUpperCase();
    if (record.rating !== undefined) payload.rating = clampRating(Number(record.rating));
    if (record.quantity !== undefined) payload.quantity = clampQuantity(Number(record.quantity));
    if (record.averagePrice !== undefined) payload.average_price = Math.max(0, Number(record.averagePrice));

    const { data, error } = await supabase
      .from(TABLE)
      .update(payload)
      .eq('id', record.id)
      .eq('user_id', userId)
      .select('id,asset_class_id,ticker,rating,quantity,average_price,updated_at')
      .single();

    if (error) throw error;
    const mapped = mapRow(data);
    if (!mapped) throw new Error('Registro de ativo invalido');
    return mapped;
  },

  async remove(id: string): Promise<void> {
    const userId = await getUserId();
    const { error } = await supabase.from(TABLE).delete().eq('id', id).eq('user_id', userId);
    if (error) throw error;
  },
};
